import { A, pipe } from "@mobily/ts-belt"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonCopy } from "@shared/models/analytics/lesson"
import { getPossessiveForLesson } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { LessonsForCourses } from "@shared/models/user"
import { findCourseDateCustom } from "cc-frontend/lib/find-course-date"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { compact, partial } from "lodash-es"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonModels from "../../lesson/default-lesson-models"
import defaultPlanbookLessonsActivity from "./default-planbook-lessons-activity"
import defaultPlanbookLessonsContext from "./default-planbook-lessons-context"

interface Payload {
  currentUserId: string
  lessonsForCourses: Array<LessonsForCourses>
  dateString: string
  ownerIds: Array<string>
  planbookId: string
}

export default function copyAllLessonsForDayAction(payload: Payload): PreparedAction {
  return {
    name: "PLANBOOK_COPY_ALL_LESSONS_FOR_DAY",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

export function patches(payload: Payload): Array<PatchModel> {
  // See LESSON_COPY
  // IF use this again, abstract!
  let patch = Patch.create("user", payload.currentUserId).set("attributes.clipboard.day", {
    // The setting of the parentCardStack is handled in the function that calls this.
    lessonsForCourses: payload.lessonsForCourses,
  }).patch

  return [patch]
}

export function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let planbook = findFn("planbook", payload.planbookId)
  let formattedDate = moment(payload.dateString).format("ddd, MMM. D, YYYY")

  let analyticsEvents = pipe(
    payload.lessonsForCourses,
    A.filter((cardStack) => {
      let courseDate = findCourseDateCustom(
        findFn("course", cardStack.courseId),
        payload.dateString
      )
      return (
        courseDate?.attributes.cardStackId !== undefined &&
        courseDate.attributes.cardStackId !== null
      )
    }),
    A.map((cardStack): LessonCopy | null => {
      let courseDate = findCourseDateCustom(
        findFn("course", cardStack.courseId),
        payload.dateString
      )
      let cardStackId = courseDate?.attributes.cardStackId
      // We should never hit this because of the filter above ^
      if (cardStackId === undefined || cardStackId === null) return null
      let models = defaultLessonModels(
        { cardStackId: cardStackId, planbookId: payload.planbookId },
        findFn,
        userId
      )
      return {
        ...createBlankEvent(),
        root_id: payload.planbookId,
        root_type: "planbook",
        date_plan_scheduled_for: payload.dateString,
        primary_object_id: cardStackId,
        primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
        primary_verb: "copy",
        primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),
        primary_object_type: "lesson",
        primary_object_specifier: null,

        adverbial_verb: "copying",
        adverbial_object_type: "lesson",
        adverbial_object_specifier: "entire_day",
        adverbial_object_id: cardStackId,
        adverbial_possessive: getPossessiveForLesson(findFn, userId, payload.ownerIds || []),
        adverbial_object_id_and_ancestor_ids: [payload.planbookId],
      }
    }),
    (arr) => compact(arr)
  )
  return {
    title: "Copied an entire day of Lessons",
    titleForAnalytics: "Lesson - Copy Entire Day",
    description: `Copied a day's worth of lessons from ${formattedDate}`,
    context: defaultPlanbookLessonsContext(planbook, payload),
    activity: defaultPlanbookLessonsActivity(payload.planbookId, userId),
    newAnalyticsEvents: analyticsEvents,
  }
}
